import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes, FaCheckCircle, FaClock, FaSpinner, FaExclamationCircle, FaCheck, FaArrowRight, FaCircle, FaCopy } from 'react-icons/fa';
import statusTrackingService from '../services/statusTrackingService';
import LoadingSpinner from './LoadingSpinner';

interface StatusTrackingStep {
  toStatus: string;
  levelNumber: number;
}

interface StatusTrackingPopupProps {
  isVisible: boolean;
  formName: string;
  currentStatus: string;
  ticketNumber: string;
  assignedRole?: string;
  onClose: () => void;
}

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 2px solid ${props => props.theme.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  color: ${props => props.theme.textPrimary};
  border-radius: 16px 16px 0 0;
  position: sticky;
  top: 0;
  z-index: 2;
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }
`;

const Content = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const TicketInfo = styled.div`
  background: white;
  border: 2px solid ${props => props.theme.primary};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  position: sticky;
  top: 0.5rem;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(244, 0, 0, 0.1);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.textPrimary};
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CopyButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.primary};
  color: ${props => props.theme.primary};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StatusTrackingContainer = styled.div`
  position: relative;
`;

const StatusTrackingTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  font-size: 1rem;
  margin: 0 0 1.5rem 0;
  font-weight: 600;
`;

const StatusStep = styled.div<{ isActive: boolean; isCompleted: boolean; isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 1rem;
    top: 2rem;
    width: 2px;
    height: 1.5rem;
    background: ${props => props.isCompleted || props.isCurrent
      ? props.theme.primary
      : props.theme.border
    };
  }
`;

const StatusIcon = styled.div<{ isActive: boolean; isCompleted: boolean; isCurrent?: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  color: white;
  font-size: 0.9rem;
  position: relative;

  background: ${props => {
    if (props.isCompleted) return '#28a745';
    if (props.isActive || props.isCurrent) return props.theme.primary;
    return '#e9ecef';
  }};

  color: ${props => {
    if (props.isCompleted) return 'white';
    if (props.isActive || props.isCurrent) return 'white';
    return '#6c757d';
  }};

  ${props => (props.isActive || props.isCurrent) && `
    box-shadow: 0 0 0 3px rgba(244, 0, 0, 0.2);
    animation: pulse 2s infinite;
  `}

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(244, 0, 0, 0.4);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(244, 0, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(244, 0, 0, 0);
    }
  }
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div<{ isActive: boolean; isCompleted: boolean; isCurrent?: boolean }>`
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  color: ${props => {
    if (props.isCompleted) return '#28a745';
    if (props.isActive || props.isCurrent) return props.theme.primary;
    return props.theme.textSecondary;
  }};
`;

const StatusLevel = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.textSecondary};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.textSecondary};
`;

const StatusTrackingPopup: React.FC<StatusTrackingPopupProps> = ({
  isVisible,
  formName,
  currentStatus,
  ticketNumber,
  assignedRole,
  onClose
}) => {
  const [statusSteps, setStatusSteps] = useState<StatusTrackingStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(ticketNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (isVisible && formName) {
      loadStatusTracking();
    }
  }, [isVisible, formName]);

  const loadStatusTracking = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await statusTrackingService.getStatusTracking(formName);

      if (response.success && response.statusTracking) {
        // Sort by level number to ensure correct order
        const sortedSteps = response.statusTracking.sort((a, b) => a.levelNumber - b.levelNumber);
        setStatusSteps(sortedSteps);
      } else {
        setError('Failed to load status tracking information');
      }
    } catch (error: any) {
      console.error('Error loading status tracking:', error);
      setError(error.message || 'Failed to load status tracking');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStepIndex = () => {
    return statusSteps.findIndex(step =>
      step.toStatus?.toLowerCase() === currentStatus?.toLowerCase()
    );
  };

  const getStepStatus = (index: number) => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex === -1) {
      // If current status not found, check if this step matches current status exactly
      const step = statusSteps[index];
      const isCurrentStatus = step?.toStatus?.toLowerCase() === currentStatus?.toLowerCase();
      return {
        isCompleted: false,
        isActive: isCurrentStatus,
        isCurrent: isCurrentStatus
      };
    }

    return {
      isCompleted: index < currentIndex,
      isActive: index === currentIndex,
      isCurrent: index === currentIndex
    };
  };

  const getStatusIcon = (index: number) => {
    const { isCompleted, isActive, isCurrent } = getStepStatus(index);

    if (isCompleted) {
      return React.createElement(FaCheck as any, {});
    } else if (isActive || isCurrent) {
      return React.createElement(FaCheckCircle as any, {});
    } else {
      return React.createElement(FaCircle as any, {});
    }
  };

  if (!isVisible) return null;

  return (
    <Overlay isVisible={isVisible} onClick={onClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Ticket Status Tracking</Title>
          <CloseButton onClick={onClose}>
            {React.createElement(FaTimes as any, {})}
          </CloseButton>
        </Header>

        <Content>
          <TicketInfo>
            <InfoRow>
              <InfoLabel>Ticket Number:</InfoLabel>
              <InfoValue>
                {ticketNumber}
                <CopyButton onClick={copyToClipboard} title="Copy ticket number">
                  {React.createElement(FaCopy as any, { size: 14 })}
                </CopyButton>
                {copied && <span style={{ fontSize: '0.75rem', color: '#28a745' }}>Copied!</span>}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Form Type:</InfoLabel>
              <InfoValue>{formName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Current Status:</InfoLabel>
              <InfoValue>{currentStatus}</InfoValue>
            </InfoRow>
            {assignedRole && (
              <InfoRow>
                <InfoLabel>Assigned To:</InfoLabel>
                <InfoValue>{assignedRole}</InfoValue>
              </InfoRow>
            )}
          </TicketInfo>

          <StatusTrackingContainer>
            <StatusTrackingTitle>Status Progress</StatusTrackingTitle>

            {loading ? (
              <LoadingSpinner isVisible={true} />
            ) : error ? (
              <ErrorMessage>
                {React.createElement(FaExclamationCircle as any, { style: { marginRight: '0.5rem' } })}
                {error}
              </ErrorMessage>
            ) : statusSteps.length > 0 ? (
              statusSteps.map((step, index) => {
                const { isCompleted, isActive, isCurrent } = getStepStatus(index);

                return (
                  <StatusStep
                    key={`${step.levelNumber}-${step.toStatus}`}
                    isActive={isActive}
                    isCompleted={isCompleted}
                    isCurrent={isCurrent}
                  >
                    <StatusIcon isActive={isActive} isCompleted={isCompleted} isCurrent={isCurrent}>
                      {getStatusIcon(index)}
                    </StatusIcon>
                    <StatusContent>
                      <StatusTitle isActive={isActive} isCompleted={isCompleted} isCurrent={isCurrent}>
                        {step.toStatus}
                      </StatusTitle>
                      <StatusLevel>Level {step.levelNumber}</StatusLevel>
                    </StatusContent>
                  </StatusStep>
                );
              })
            ) : (
              <ErrorMessage>
                No status tracking information available for this form type.
              </ErrorMessage>
            )}
          </StatusTrackingContainer>
        </Content>
      </PopupContainer>
    </Overlay>
  );
};

export default StatusTrackingPopup;